import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    MdCheckCircleOutline,
    MdClose,
    MdContentCopy,
    MdGavel,
    MdInfoOutline,
    MdRemoveCircleOutline,
    MdSearch,
    MdWarningAmber,
} from 'react-icons/md';

import { formatCollectedValue, NOT_COLLECTED_LABEL, PartialCollection } from './utils/partialCollection';
import {
    getRecommendationBadgeClass,
    getRecommendationLabel,
    normalizeRecommendation,
} from './utils/statusLabels';

const NO_RESTRICTION = 'Nenhuma restrição PLD encontrada';

export interface RulesModalProvider {
    recommendation: string;
    reasons: string[];
    // AF-4082: dado parcial de item INTERRUPTED
    partialCollection?: PartialCollection | null;
    interrupted?: boolean;
}

export type ProviderSectionMode =
    | 'NOT_CONSULTED'
    | 'NO_DATA'
    | 'INTERRUPTED_PARTIAL'
    | 'INTERRUPTED_EMPTY'
    | 'RULES'
    | 'CLEAN';

/**
 * AF-4082: decide o que a seção do provider mostra. Item INTERRUPTED nunca cai em
 * 'CLEAN' ("Nenhuma restrição"): o motor devolve PENDING ou REJECTED para ele, e o
 * flag `interrupted` identifica o item.
 */
export const getProviderSectionMode = (data: RulesModalProvider | null): ProviderSectionMode => {
    if (!data) return 'NOT_CONSULTED';
    const recommendation = normalizeRecommendation(data.recommendation);
    if (data.interrupted === true || recommendation === 'INTERRUPTED') {
        return data.partialCollection ? 'INTERRUPTED_PARTIAL' : 'INTERRUPTED_EMPTY';
    }
    if (recommendation === 'NO_DATA') return 'NO_DATA';
    const rules = (data.reasons ?? []).filter((reason) => reason !== NO_RESTRICTION);
    return rules.length > 0 ? 'RULES' : 'CLEAN';
};

export interface RulesModalDocument {
    document: string;
    entityName?: string;
    providers: Record<string, RulesModalProvider | null>;
}

/**
 * Separa a regra em descrição e nome.
 *
 * O motor devolve `f"{description} ({rule_name})"` (ver DynamicRulesEngine.get_recommendations),
 * então o nome da regra é o ÚLTIMO par de parênteses — a descrição pode conter parênteses.
 * Quando a regra não tem descrição, o texto todo é o nome.
 *
 * `description` e `name` são colunas distintas de business_rules e podem ter o mesmo valor.
 * Os dois continuam sendo exibidos: igualdade é um estado válido do cadastro, não
 * duplicação. Quem diferencia é o rótulo na exibição — sem ele, dois textos idênticos
 * pareciam erro de renderização.
 */
export const parseRule = (raw: string): { description: string; name?: string } => {
    const text = (raw ?? '').trim();
    if (!text.endsWith(')')) return { description: text };

    const openIndex = text.lastIndexOf('(');
    if (openIndex <= 0) return { description: text };

    const description = text.slice(0, openIndex).trim();
    const name = text.slice(openIndex + 1, -1).trim();

    if (!description || !name) return { description: text };
    return { description, name };
};

const PartialCollectionList: React.FC<{ collection: PartialCollection }> = ({ collection }) => (
    <div className="space-y-2">
        <p className="flex items-center gap-2 px-1 py-1 text-sm text-gray-600 dark:text-gray-300">
            <MdInfoOutline size={18} aria-hidden="true" />
            Processamento interrompido: a recomendação considera apenas os dados coletados.
        </p>
        <ul className="space-y-1">
            {collection.collected.map((item) => (
                <li
                    key={item.field}
                    className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-800"
                >
                    <span className="text-gray-700 dark:text-gray-300">{item.label}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                        {formatCollectedValue(item.value)}
                    </span>
                </li>
            ))}
            {collection.pending.map((item) => (
                <li
                    key={item.field}
                    className="flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-sm dark:border-amber-900/60 dark:bg-amber-950/40"
                >
                    <span className="text-gray-700 dark:text-gray-300">{item.label}</span>
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                        {NOT_COLLECTED_LABEL}
                    </span>
                </li>
            ))}
        </ul>
    </div>
);

const getCopySummary = (data: RulesModalProvider | null, rulesCount: number): string => {
    const mode = getProviderSectionMode(data);
    if (mode === 'INTERRUPTED_PARTIAL') return 'interrompido (dados parciais)';
    if (mode === 'INTERRUPTED_EMPTY') return 'interrompido (sem dados coletados)';
    return rulesCount === 0 ? 'sem restrições' : '';
};

const ProviderSection: React.FC<{
    provider: string;
    data: RulesModalProvider | null;
    searchTerm: string;
}> = ({ provider, data, searchTerm }) => {
    const rules = (data?.reasons ?? []).filter((reason) => reason !== NO_RESTRICTION);

    const visibleRules = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return rules;
        return rules.filter((rule) => rule.toLowerCase().includes(term));
    }, [rules, searchTerm]);

    // Com busca ativa, esconde a seção que não tem resultado
    if (searchTerm.trim() && visibleRules.length === 0) return null;

    const mode = getProviderSectionMode(data);
    const notConsulted = mode === 'NOT_CONSULTED';
    const interrupted = mode === 'INTERRUPTED_PARTIAL' || mode === 'INTERRUPTED_EMPTY';
    // AF-3460: bureau consultado com sucesso, porém sem informação para o documento
    const noData = mode === 'NO_DATA';

    return (
        <section className="rounded-lg border border-gray-200 dark:border-slate-600">
            <header className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2.5 dark:border-slate-600 dark:bg-slate-700/50">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{provider}</h4>
                {data && (
                    <span className={getRecommendationBadgeClass(data.recommendation)}>
                        {getRecommendationLabel(data.recommendation)}
                    </span>
                )}
                {/*
                  Contador só quando há regras. Com zero regras o corpo já diz "Nenhuma
                  restrição encontrada" e o badge já diz "Aprovado" — repetir "Sem
                  restrições" aqui era a terceira forma de afirmar a mesma coisa.
                */}
                {notConsulted && (
                    <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
                        Lote não selecionado
                    </span>
                )}
                {!notConsulted && noData && (
                    <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
                        Nada a avaliar
                    </span>
                )}
                {!notConsulted && !noData && !interrupted && rules.length > 0 && (
                    <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
                        {rules.length} {rules.length === 1 ? 'regra' : 'regras'}
                    </span>
                )}
            </header>

            <div className="p-3">
                {notConsulted && (
                    <p className="px-1 py-2 text-sm text-gray-400 dark:text-gray-500">
                        Nenhum lote deste provider foi selecionado para o parecer.
                    </p>
                )}

                {/*
                  AF-3460: "sem dados" deixou de ser confundido com "verificado e limpo".
                  Antes os dois caíam em "Nenhuma restrição encontrada", o que sugeria que o
                  bureau tinha sido consultado e nada foi encontrado — quando na verdade não
                  havia o que consultar.
                */}
                {!notConsulted && noData && (
                    <p className="flex items-center gap-2 px-1 py-2 text-sm text-gray-600 dark:text-gray-400">
                        <MdRemoveCircleOutline size={18} aria-hidden="true" />
                        Sem dados para executar análise
                    </p>
                )}

                {/*
                  Estado "sem restrição" com tratamento visual próprio. Antes ele era
                  renderizado como um item cinza igual ao de uma regra violada, o que fazia
                  provider limpo e provider com violação parecerem a mesma coisa.
                */}
                {mode === 'INTERRUPTED_PARTIAL' && data?.partialCollection && (
                    <PartialCollectionList collection={data.partialCollection} />
                )}

                {mode === 'INTERRUPTED_EMPTY' && (
                    <p className="flex items-center gap-2 px-1 py-2 text-sm text-gray-600 dark:text-gray-400">
                        <MdInfoOutline size={18} aria-hidden="true" />
                        Processamento interrompido, sem dados coletados
                    </p>
                )}

                {mode === 'CLEAN' && (
                    <p className="flex items-center gap-2 px-1 py-2 text-sm text-green-700 dark:text-green-400">
                        <MdCheckCircleOutline size={18} aria-hidden="true" />
                        Nenhuma restrição encontrada
                    </p>
                )}

                {visibleRules.length > 0 && (
                    <ul className="space-y-2">
                        {visibleRules.map((rule, index) => {
                            const { description, name } = parseRule(rule);
                            return (
                                <li
                                    key={`${rule}-${index}`}
                                    className="flex gap-2.5 rounded-md border border-orange-200 bg-orange-50 p-3 dark:border-orange-900/60 dark:bg-orange-950/40"
                                >
                                    <MdWarningAmber
                                        className="mt-0.5 shrink-0 text-orange-500 dark:text-orange-400"
                                        size={18}
                                        aria-hidden="true"
                                    />
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                            {description}
                                        </p>
                                        {name && (
                                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                <span className="uppercase tracking-wide">Regra:</span>{' '}
                                                <span className="font-mono">{name}</span>
                                            </p>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </section>
    );
};

/**
 * AF-3418: modal de regras acionadas do parecer.
 *
 * O anterior recebia uma lista de strings já achatadas no formato "PROVIDER: motivo",
 * perdendo a estrutura por provider. Aqui o documento é passado inteiro, o que permite
 * agrupar por provider, mostrar a recomendação de cada um e distinguir visualmente
 * "sem restrição" de regra violada.
 */
const TriggeredRulesModal: React.FC<{
    document: RulesModalDocument | null;
    onClose: () => void;
}> = ({ document: doc, onClose }) => {
    const [search, setSearch] = useState('');
    const [copied, setCopied] = useState(false);
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!doc) return;
        setSearch('');
        setCopied(false);
        closeButtonRef.current?.focus();

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [doc, onClose]);

    if (!doc) return null;

    const providerEntries = Object.entries(doc.providers);
    const totalRules = providerEntries.reduce(
        (total, [, data]) =>
            total + (data?.reasons ?? []).filter((reason) => reason !== NO_RESTRICTION).length,
        0
    );

    /** Texto plano para colar nas observações ou num e-mail. */
    const copyRules = async () => {
        const lines: string[] = [`Documento: ${doc.document}`];
        if (doc.entityName) lines.push(`Nome: ${doc.entityName}`);
        lines.push('');

        providerEntries.forEach(([provider, data]) => {
            const rules = (data?.reasons ?? []).filter((reason) => reason !== NO_RESTRICTION);
            lines.push(`${provider}: ${getCopySummary(data, rules.length)}`.trim());
            rules.forEach((rule) => lines.push(`  - ${rule}`));
        });

        try {
            await navigator.clipboard.writeText(lines.join('\n'));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            setCopied(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onMouseDown={(event) => {
                // Fecha ao clicar no backdrop, não em cliques que começaram dentro do painel
                if (event.target === event.currentTarget) onClose();
            }}
        >
            <div
                className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-xl dark:bg-slate-800"
                role="dialog"
                aria-modal="true"
                aria-labelledby="rules-modal-title"
            >
                {/* Cabeçalho fixo: antes o max-h ficava na caixa toda e o título rolava junto */}
                <header className="flex items-start gap-3 border-b border-gray-200 px-5 py-4 dark:border-slate-700">
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-slate-700 dark:text-blue-400">
                        <MdGavel size={20} aria-hidden="true" />
                    </span>
                    <div className="min-w-0 flex-1">
                        <h3
                            id="rules-modal-title"
                            className="text-base font-semibold text-gray-900 dark:text-gray-100"
                        >
                            Regras acionadas
                        </h3>
                        <p className="mt-0.5 truncate text-sm text-gray-600 dark:text-gray-400">
                            <span className="font-medium text-gray-800 dark:text-gray-200">
                                {doc.document}
                            </span>
                            {doc.entityName ? ` · ${doc.entityName}` : ''}
                        </p>
                    </div>
                    <button
                        ref={closeButtonRef}
                        type="button"
                        onClick={onClose}
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-slate-700 dark:hover:text-gray-200"
                        aria-label="Fechar"
                    >
                        <MdClose size={20} />
                    </button>
                </header>

                {/* Busca só aparece quando há volume que justifique */}
                {totalRules > 4 && (
                    <div className="border-b border-gray-200 px-5 py-3 dark:border-slate-700">
                        <div className="relative">
                            <MdSearch
                                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                                size={18}
                                aria-hidden="true"
                            />
                            <input
                                type="text"
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Buscar regra"
                                aria-label="Buscar regra"
                                className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-100"
                            />
                        </div>
                    </div>
                )}

                {/* Somente o corpo rola */}
                <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
                    {providerEntries.map(([provider, data]) => (
                        <ProviderSection
                            key={provider}
                            provider={provider}
                            data={data}
                            searchTerm={search}
                        />
                    ))}

                    {search.trim() && (
                        <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                            {providerEntries.every(([, data]) => {
                                const rules = (data?.reasons ?? []).filter((r) => r !== NO_RESTRICTION);
                                return !rules.some((r) =>
                                    r.toLowerCase().includes(search.trim().toLowerCase())
                                );
                            })
                                ? 'Nenhuma regra encontrada para a busca'
                                : ''}
                        </p>
                    )}
                </div>

                <footer className="flex items-center justify-between gap-3 border-t border-gray-200 px-5 py-3 dark:border-slate-700">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                        {totalRules === 0
                            ? 'Nenhuma regra acionada'
                            : `${totalRules} ${totalRules === 1 ? 'regra acionada' : 'regras acionadas'}`}
                    </span>
                    <div className="flex gap-2">
                        {totalRules > 0 && (
                            <button
                                type="button"
                                onClick={copyRules}
                                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:text-gray-200 dark:hover:bg-slate-700"
                            >
                                <MdContentCopy size={16} aria-hidden="true" />
                                {copied ? 'Copiado' : 'Copiar'}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
                        >
                            Fechar
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default TriggeredRulesModal;
